import Add from "@mui/icons-material/Add";
import {
  CircularProgress,
  IconButton,
  List,
  Stack,
  Typography,
} from "@mui/material";
import { usePagePrefs } from "@pepsico/pref-permissions";
import { useIsMutating } from "@tanstack/react-query";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import type { GrowerGeoResponse } from "api/model";
import { usePlotMap } from "context/PlotMap";

import GrowerBoundaryItem from "./GrowerBoundaryItem";

interface GrowerBoundarySectionProps {}

const GrowerBoundarySection: React.FC<GrowerBoundarySectionProps> = () => {
  const [prefs] = usePagePrefs();
  const {
    boundary: {
      editingGrowerGeoId,
      enterGrowerBoundaryMode,
      isDirty,
      isGrowerBoundaryMode,
      isSiteBoundaryMode,
      loadSaved,
      points,
    },
  } = usePlotMap();

  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const {
    data: growers = [],
    isFetching,
    isPending,
  } = useGetGrowersGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const creatingCount = useIsMutating({ mutationKey: ["createGrowerGeo"] });
  const deletingCount = useIsMutating({ mutationKey: ["deleteGrowerGeo"] });
  const updatingCount = useIsMutating({
    mutationKey: ["updateGrowerGeoBoundary"],
  });
  const isSaving = creatingCount + deletingCount + updatingCount > 0;
  const isDrawingNewGrower = isGrowerBoundaryMode && editingGrowerGeoId == null;
  const visibleGrowers = isGrowerBoundaryMode
    ? growers.filter((grower) => grower.id === editingGrowerGeoId)
    : growers;
  const isLoadingGrowers =
    locationUuid !== "" && isPending && growers.length === 0;
  const showEmptyState =
    locationUuid !== "" &&
    !isPending &&
    growers.length === 0 &&
    !isGrowerBoundaryMode &&
    !isSiteBoundaryMode &&
    !isDirty &&
    points.length === 0;
  const canAddGrower =
    locationUuid !== "" &&
    !isSiteBoundaryMode &&
    !isSaving &&
    !isGrowerBoundaryMode &&
    !isDirty;

  const handleAddGrowerBoundary = () => {
    if (!canAddGrower) {
      return;
    }

    loadSaved([], "");
    enterGrowerBoundaryMode(null);
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <Typography
          component="h3"
          sx={{ fontWeight: "bold" }}
          variant="subtitle2"
        >
          GROWER BOUNDARIES
        </Typography>
        <IconButton
          aria-label="Add new grower boundary"
          disabled={!canAddGrower}
          onClick={handleAddGrowerBoundary}
          size="small"
        >
          <Add fontSize="small" />
        </IconButton>
      </Stack>
      {isLoadingGrowers && (
        <CircularProgress aria-label="Loading grower boundaries" size={24} />
      )}
      {showEmptyState && (
        <Typography color="text.secondary" variant="body2">
          No grower boundaries yet. Draw one on the map around the grower&apos;s
          field.
        </Typography>
      )}
      {!isLoadingGrowers && !showEmptyState && (
        <>
          {isFetching && (
            <CircularProgress
              aria-label="Refreshing grower boundaries"
              size={20}
            />
          )}
          <List
            aria-label="Grower boundaries"
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            {visibleGrowers.map((grower) => (
              <GrowerBoundaryItem key={grower.id} growerGeoId={grower.id} />
            ))}
            {isDrawingNewGrower && <GrowerBoundaryItem growerGeoId={null} />}
          </List>
        </>
      )}
    </Stack>
  );
};

export default GrowerBoundarySection;
